import { newError } from '../errors';
import * as actionTypes from '../../constants/actions';
import { getClassRoomPost } from '../../utils/data';
import { fetchTagPostsSuccess } from '../tags/tagPost';
import fetchReactionsSuccess from '../../reactions/operations/fetchReactionsSuccess.operation';
import { fetchTagsSuccess } from '../tags/tag';
import { fetchCommentsSuccess } from '../comment/fetch';
import deletePostApi from '../../api/posts/delete';
import fetchPostByIdApi from '../../api/posts/fetchById';
import editPostApi from '../../api/posts/edit';
import getSpaceTagsApi from '../../api/tags/getSpaceTags';
import getFeed from '../../api/posts/feed';
import { currentClassRoomId } from '../../selectors/classroom';
import { unsetPostImportant, fetchImportantPosts } from './importantPosts';
import { fetchScheduledPostsSuccess } from './scheduledPosts';
import { groupCommentByPostReducer } from './utils';
import showToast from '../../toasts/operations/showToast.operation';

// Fetch classRoom content
const fetchClassRoomContentStart = (reset, spaceId, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_CONTENT,
    status: 'started',
    reset,
    spaceId,
  });
};

const fetchClassRoomContentSuccess = (content, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_CONTENT,
    status: 'success',
    content,
  });
};

const fetchClassRoomContentError = (error, dispatch) => {
  dispatch(newError(error, 'fetchClassRoomContent'));
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_CONTENT,
    status: 'error',
    error,
  });
};

export const fetchClassRoomContent =
  ({ classRoom, reset = false, latest = false, tagId }) =>
  async (dispatch) => {
    if (!classRoom) {
      const err = { message: 'You need to provide the classRoom' };
      fetchClassRoomContentError(err, dispatch);
      return;
    }

    const spaceId = classRoom.objectId;

    const until =
      !reset && classRoom.posts.length > 0 && !latest
        ? classRoom.oldestPostDate
        : new Date();

    fetchClassRoomContentStart(reset, spaceId, dispatch);
    dispatch(fetchImportantPosts({ spaceId, tagId }));

    try {
      const [data, scheduledData, tags] = await Promise.all([
        getFeed({ spaceId, until, tagId }),
        getFeed({ spaceId, scheduled: true }),
        getSpaceTagsApi({ spaceId }),
      ]);

      const { tagPosts, posts, comments, reactions } = data;
      fetchClassRoomContentSuccess(posts, dispatch);
      fetchTagsSuccess({ tags, spaceId }, dispatch);
      fetchTagPostsSuccess(tagPosts, dispatch);
      fetchScheduledPostsSuccess(dispatch, scheduledData.posts);
      fetchTagPostsSuccess(scheduledData.tagPosts, dispatch);

      // accumulate all comments per post
      const commentPosts = comments.reduce(groupCommentByPostReducer, {});
      Object.keys(commentPosts).forEach((postId) => {
        if (postId) {
          fetchCommentsSuccess(postId, commentPosts[postId], dispatch);
        }
      });

      dispatch(fetchReactionsSuccess(reactions));
    } catch (error) {
      fetchClassRoomContentError(error, dispatch);
      throw error;
    }
  };

// Fetch single classRoom post
const fetchClassRoomPostStart = (dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_POST,
    status: 'started',
  });
};

const fetchClassRoomPostSuccess = (post, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_POST,
    status: 'success',
    post,
  });
};

const fetchClassRoomPostError = (error, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_POST,
    status: 'error',
    error,
  });
};

const getFetchPostErrorToast = (error) => {
  if (error.response.status === 404) {
    return {
      emoji: '🙈',
      message: "Oops! The post doesn't exist",
    };
  }
  return {
    emoji: '😬',
    message: "Oops! We could't load the post",
  };
};

export const fetchClassRoomPost =
  (objectId, ignoreCache = false) =>
  async (dispatch, getState) => {
    if (!objectId) {
      const err = { message: 'You need to provide the post id' };
      return fetchClassRoomPostError(err, dispatch);
    }

    const post = getClassRoomPost(getState(), objectId);
    const spaceId = currentClassRoomId(getState());

    if (!ignoreCache && post) {
      return new Promise((resolve) => {
        resolve(post);
      });
    }
    fetchClassRoomPostStart(dispatch);

    try {
      const {
        post: postFetched,
        tagPosts,
        tags,
        reactions,
        comments,
      } = await fetchPostByIdApi(objectId);

      fetchTagsSuccess({ tags, spaceId }, dispatch);
      fetchTagPostsSuccess(tagPosts, dispatch);
      fetchReactionsSuccess(reactions, dispatch);
      fetchClassRoomPostSuccess(postFetched, dispatch);
      fetchCommentsSuccess(objectId, comments, dispatch);

      return postFetched;
    } catch (error) {
      const toast = getFetchPostErrorToast(error);
      dispatch(showToast(toast));
      return fetchClassRoomPostError(error, dispatch);
    }
  };

// Delete classRoom post
const deleteClassRoomPostStart = (id) => (dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_DELETE_POST,
    status: 'started',
    id,
  });
};

const deleteClassRoomPostSuccess = (post, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_DELETE_POST,
    status: 'success',
    post,
  });
};

const deleteClassRoomPostError =
  ({ id, error }) =>
  (dispatch) => {
    dispatch(newError(error, 'deleteClassRoomPost'));
    dispatch({
      type: actionTypes.CLASSROOM_DELETE_POST,
      status: 'error',
      error,
      id,
    });
  };

export const deleteClassRoomPost = (objectId) => async (dispatch, getState) => {
  if (!objectId) {
    const err = { message: 'You need to provide the post id' };
    return deleteClassRoomPostError(err, dispatch);
  }

  const post = getClassRoomPost(getState(), objectId);
  post.className = 'UBClassRoomPost';

  deleteClassRoomPostStart(objectId)(dispatch);

  if (post.important) {
    await dispatch(unsetPostImportant(post.objectId));
  }

  try {
    await deletePostApi(objectId);
    return deleteClassRoomPostSuccess(post, dispatch);
  } catch (error) {
    throw deleteClassRoomPostError({ error, id: objectId })(dispatch);
  }
};

// edit classRoom post
const editClassRoomPostStart = (dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_EDIT_POST,
    status: 'started',
  });
};

const editClassRoomPostSuccess = (post, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_EDIT_POST,
    status: 'success',
    post,
  });
};

const editClassRoomPostError = (error, dispatch) => {
  dispatch(newError(error, 'editClassRoomPost'));
  dispatch({
    type: actionTypes.CLASSROOM_EDIT_POST,
    status: 'error',
    error,
  });
};

export const editClassRoomPost =
  ({ postId, content, scheduledFor }) =>
  async (dispatch) => {
    if (!postId) {
      const err = { message: 'You need to provide the post id' };
      return editClassRoomPostError(err, dispatch);
    }

    editClassRoomPostStart(dispatch);

    try {
      const savedPost = await editPostApi({
        postId,
        content,
        scheduledFor,
      });
      return editClassRoomPostSuccess(savedPost, dispatch);
    } catch (error) {
      return editClassRoomPostError(error, dispatch);
    }
  };
